import { BffApiClient } from '@/common/services/api/bffClient';
import { agendaEndpoints } from '../endpoints/agendaEndpoints';
import type {
  CalendarInterval,
  AppointmentSubmission,
  CalendarEvent,
  AgendaApiResponse,
  PersonalEventApiResponse,
  ActivityApiResponse,
  SessionApiResponse,
  CreatePersonalEventRequest,
  PersonalEvent,
  ActivityEvent,
  SessionEvent,
} from '../types';

import { EventType, InterventionPlace, RsvpStatus } from '../types';

export class AgendaService {
  private api: BffApiClient;

  constructor() {
    this.api = BffApiClient.getInstance();
  }

  private mapPersonalEventResponse(event: PersonalEventApiResponse): PersonalEvent {
    return {
      id: event.id,
      type: EventType.PersonalEvent,
      start: event.start,
      end: event.end,
      label: event.title,
      description: event.description,
      locations: event.locations,
      isMine: event.isOrganizer,
      rsvp: event.attendees.find((a) => a.isMe)?.responseType === 'Accepted',
      teamsLink: event.teamsUrl || undefined,
      attendees: event.attendees.map((a) => a.mail),
    };
  }

  private mapActivityResponse(event: ActivityApiResponse): ActivityEvent {
    return {
      id: event.id,
      type: EventType.Activity,
      start: event.start,
      end: event.end,
      label: event.label,
      description: event.description,
      locations: event.locations,
      teamsLink: event.teamsLink,
      zoomLink: event.zoomLink,
      moodleLink: event.moodleLink,
      teacher: event.teacher,
      interventionPlace: this.mapInterventionPlace(event.interventionPlace),
      interventionPlaceAddress: event.interventionPlaceAddress,
      moreInfo: event.moreInfo,
    };
  }

  private mapSessionResponse(event: SessionApiResponse): SessionEvent {
    return {
      id: event.id,
      type: EventType.Session,
      start: event.start,
      end: event.end,
      label: event.label,
      description: event.description,
      locations: event.locations,
    };
  }

  private mapInterventionPlace(place?: string): InterventionPlace | undefined {
    if (!place) return undefined;

    const mapping: Record<string, InterventionPlace> = {
      NotSet: InterventionPlace.NotSet,
      InPerson: InterventionPlace.InPerson,
      Online: InterventionPlace.Online,
      OnlineTeacher: InterventionPlace.OnlineTeacher,
      Outside: InterventionPlace.Outside,
    };

    return mapping[place];
  }

  private mapAppointmentSubmissionToCreateRequest(
    appointment: AppointmentSubmission
  ): CreatePersonalEventRequest {
    return {
      Title: appointment.label,
      Description: appointment.description || '',
      Start: appointment.start,
      End: appointment.end,
      LocationName: appointment.locations?.map((loc) => loc.libelle).join(', ') || '',
      Attendees: appointment.attendees || [],
      IsOnlineMeeting: appointment.isOnlineMeeting || false,
    };
  }

  async fetchEvents(interval?: CalendarInterval): Promise<CalendarEvent[]> {
    if (!interval) {
      const todayDate = new Date();
      const startOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth() - 4, 1);
      const endOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth() + 6, 0);
      interval = {
        start: startOfMonth.toISOString(),
        end: endOfMonth.toISOString(),
      };
    }

    const queryParams = new URLSearchParams({
      startDate: interval.start,
      endDate: interval.end,
    });

    const response = await this.api.get<AgendaApiResponse>(
      `${agendaEndpoints.getEvents}?${queryParams.toString()}`
    );

    const personalEvents = response.data.personalEvents
      .filter((e) => !e.isCanceled)
      .map((e) => this.mapPersonalEventResponse(e));

    const activities = response.data.activities.map((e) => this.mapActivityResponse(e));

    const sessions = response.data.sessions.map((e) => this.mapSessionResponse(e));

    return [...personalEvents, ...activities, ...sessions];
  }

  async updateEvents(interval: CalendarInterval): Promise<CalendarEvent[]> {
    return this.fetchEvents(interval);
  }

  async createAppointment(appointment: AppointmentSubmission): Promise<PersonalEvent> {
    const createRequest = this.mapAppointmentSubmissionToCreateRequest(appointment);

    const response = await this.api.post<PersonalEventApiResponse>(
      agendaEndpoints.createPersonalEvents,
      createRequest
    );

    return this.mapPersonalEventResponse(response.data);
  }

  async updateAppointment(id: string, appointment: AppointmentSubmission): Promise<PersonalEvent> {
    const updateRequest = this.mapAppointmentSubmissionToCreateRequest(appointment);

    const response = await this.api.put<PersonalEventApiResponse>(
      `${agendaEndpoints.updatePersonalEvent(id)}`,
      updateRequest
    );

    return this.mapPersonalEventResponse(response.data);
  }

  async deleteAppointment(id: string): Promise<void> {
    await this.api.delete<void>(`${agendaEndpoints.deletePersonalEvent(id)}`);
  }

  async acceptAppointmentRsvp(id: string, comments: string = ''): Promise<void> {
    await this.api.put<void>(
      `${agendaEndpoints.acceptOrDeclineEventInvitation(id, RsvpStatus.Accept, comments)}`
    );
  }

  async declineAppointmentRsvp(id: string, comments: string = ''): Promise<void> {
    await this.api.put<void>(
      `${agendaEndpoints.acceptOrDeclineEventInvitation(id, RsvpStatus.Decline, comments)}`
    );
  }
}

export const agendaService = new AgendaService();
